import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, ArrowRight, Copy, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import { ResponsiveContainer, BarChart, Bar, XAxis, Tooltip } from 'recharts';
import { buildEmail } from './emailTemplateBuilder';

/**
 * Highlights policies whose renewal date is within the next 30 days so the
 * agent can send renewal letters/emails before the customer's cover lapses.
 */

const RenewalsWidget = ({ policies }) => {
  const { toast } = useToast();

  const today = Date.now();
  const in30 = today + 30 * 24 * 60 * 60 * 1000;
  const due = (policies || [])
    .filter((p) => p.status !== 'Cancelled' && p.paymentStatus !== 'Cancelled' && p.policyEnd)
    .map((p) => ({ ...p, endTs: new Date(p.policyEnd).getTime() }))
    .filter((p) => p.endTs <= in30) // includes expired ones so overdue renewals surface too
    .sort((a, b) => a.endTs - b.endTs);

  // Build a 12-month look-ahead sparkline of upcoming renewals grouped by end month.
  const sparkline = (() => {
    const now = new Date();
    const buckets = [];
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      buckets.push({ key: `${d.getFullYear()}-${d.getMonth()}`, label: d.toLocaleDateString('en-GB', { month: 'short' }), year: d.getFullYear(), month: d.getMonth(), count: 0 });
    }
    (policies || []).forEach((p) => {
      if (!p.policyEnd || p.status === 'Cancelled') return;
      const end = new Date(p.policyEnd);
      const b = buckets.find((x) => x.year === end.getFullYear() && x.month === end.getMonth());
      if (b) b.count += 1;
    });
    return buckets;
  })();
  const sparklineTotal = sparkline.reduce((s, b) => s + b.count, 0);

  const copyEmail = async (p) => {
    const e = buildEmail('renewal', p);
    try {
      // Prefer rich HTML so it retains the design when pasted into Outlook / Gmail.
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        const htmlBlob = new Blob([e.html], { type: 'text/html' });
        const textBlob = new Blob([`Subject: ${e.subject}\n\n${e.text}`], { type: 'text/plain' });
        await navigator.clipboard.write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': textBlob })]);
      } else {
        await navigator.clipboard.writeText(`Subject: ${e.subject}\n\n${e.text}`);
      }
      toast({ title: 'Renewal email copied', description: 'Designed email + fallback text ready to paste.' });
    } catch {
      toast({ title: 'Could not copy — please try again' });
    }
  };

  if (due.length === 0) {
    return (
      <div data-testid="renewals-widget-empty" className="bg-white rounded-2xl border border-[#1F0068]/5 p-5 md:p-6">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-[#1F0068]/60">
            <CalendarClock className="w-4 h-4 text-[#00b67a]" /> Renewals due (next 30 days)
          </div>
          {sparklineTotal > 0 && (
            <div className="text-xs text-[#1F0068]/60"><TrendingUp className="w-3 h-3 inline text-[#00b67a] mr-1" /> {sparklineTotal} coming up in the next 12 months</div>
          )}
        </div>
        <div className="mt-3 text-sm text-[#1F0068]/70">Nothing to send this month. We’ll show renewal reminders here as they come due.</div>
        {sparklineTotal > 0 && (
          <div data-testid="renewals-sparkline-empty" className="mt-3 h-20">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={sparkline}>
                <XAxis dataKey="label" fontSize={10} stroke="#1F006860" axisLine={false} tickLine={false} interval={0} />
                <Tooltip cursor={{ fill: '#FF5C3915' }} contentStyle={{ borderRadius: 10, border: '1px solid #1F006820', fontSize: 12 }} />
                <Bar dataKey="count" name="Renewals due" fill="#1F0068" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    );
  }

  return (
    <div data-testid="renewals-widget" className="bg-white rounded-2xl border border-[#1F0068]/5 p-5 md:p-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-bold text-[#FF5C39]">
            <CalendarClock className="w-4 h-4" /> Renewals due (next 30 days)
          </div>
          <h3 className="text-lg font-bold text-[#1F0068] mt-1">{due.length} polic{due.length === 1 ? 'y' : 'ies'} to renew</h3>
        </div>
        <div className="text-xs text-[#1F0068]/60 text-right"><TrendingUp className="w-3 h-3 inline text-[#00b67a] mr-1" /> {sparklineTotal} coming up in the next 12 months</div>
      </div>

      <div data-testid="renewals-sparkline" className="mt-4 h-16">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={sparkline}>
            <XAxis dataKey="label" fontSize={10} stroke="#1F006860" axisLine={false} tickLine={false} interval={0} />
            <Tooltip cursor={{ fill: '#FF5C3915' }} contentStyle={{ borderRadius: 10, border: '1px solid #1F006820', fontSize: 12 }} formatter={(v) => `${v} renewal${v === 1 ? '' : 's'}`} />
            <Bar dataKey="count" name="Renewals due" fill="#1F0068" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-2">
        {due.slice(0, 6).map((p) => {
          const days = Math.round((p.endTs - today) / (24 * 60 * 60 * 1000));
          const overdue = days < 0;
          return (
            <div key={p.id} data-testid={`renewal-row-${p.id}`} className="bg-[#FFF6EB] rounded-xl p-3 flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-[#1F0068] truncate">{p.firstName} {p.lastName}</div>
                <div className="text-xs text-[#1F0068]/70">
                  {p.planNumber} · £{Number(p.amount || 0).toFixed(2)} · <span className={overdue ? 'text-[#FF5C39] font-semibold' : ''}>{overdue ? `${Math.abs(days)}d overdue` : `${days}d to go`}</span>
                </div>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <Button
                  data-testid={`renewal-copy-${p.id}`}
                  size="sm"
                  variant="outline"
                  onClick={() => copyEmail(p)}
                  className="rounded-full text-xs h-8 px-3"
                >
                  <Copy className="w-3 h-3 mr-1" /> Copy email
                </Button>
                <Link to={`/agent/policy/${p.id}`}>
                  <Button size="sm" className="rounded-full bg-[#1F0068] hover:bg-[#2A0C7A] text-white text-xs h-8 px-3">
                    Open <ArrowRight className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </div>
            </div>
          );
        })}
      </div>
      {due.length > 6 && (
        <div className="mt-3 text-xs text-[#1F0068]/70">+ {due.length - 6} more — filter by policy end date to see everything.</div>
      )}
    </div>
  );
};

export default RenewalsWidget;
